export const WORTH_WATCHING_CACHE_KEY = 'shinai:worth-watching:v1'
export const WORTH_WATCHING_CACHE_VERSION = 1
export const WORTH_WATCHING_SIZE = 30
export const WORTH_WATCHING_CINEMA_SHARE = 0.7
export const WORTH_WATCHING_ANIME_SHARE = 0.3
export const WORTH_WATCHING_FRESH_TTL = 6 * 60 * 60 * 1000
export const WORTH_WATCHING_MAX_STALE = 48 * 60 * 60 * 1000

const DAY_MS = 24 * 60 * 60 * 1000
const RECENT_YEARS = 6
const CACHE_SOURCE_LIMIT = 100
const VALID_KINDS = new Set(['movie', 'series', 'anime'])
const LOW_PLAYBACK_QUALITY = /(^|[\s._-])(camrip|cam|ts|telesync|tc)([\s._-]|$)/i

function finiteNumber(value) {
    if (value === null || value === undefined || value === '') return null
    let parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
}

function text(value, fallback = '') {
    let normalized = String(value || '').trim()
    return normalized || fallback
}

function httpUrl(value) {
    let candidate = typeof value === 'object' && value ? value.url : value
    if (!/^https?:\/\//i.test(String(candidate || ''))) return null
    try {
        return new URL(String(candidate)).toString()
    } catch (_) {
        return null
    }
}

function normalizedIds(ids = {}) {
    return {
        kinopoisk: finiteNumber(ids.kinopoisk),
        imdb: text(ids.imdb) || null,
        tmdb: finiteNumber(ids.tmdb),
        shikimori: text(ids.shikimori) || null,
    }
}

function normalizedSignals(signals = {}) {
    let ratings = signals.ratings || {}
    let votes = signals.votes || {}
    return {
        ratings: {
            kinopoisk: finiteNumber(ratings.kinopoisk),
            imdb: finiteNumber(ratings.imdb),
            shikimori: finiteNumber(ratings.shikimori),
        },
        votes: {
            kinopoisk: finiteNumber(votes.kinopoisk),
            imdb: finiteNumber(votes.imdb),
            shikimori: finiteNumber(votes.shikimori),
        },
        popularity: finiteNumber(signals.popularity),
        premiereAt: text(signals.premiereAt) || null,
        updatedAt: finiteNumber(signals.updatedAt),
        status: text(signals.status, 'unknown'),
        playbackQuality: text(signals.playbackQuality) || null,
        sourceRank: finiteNumber(signals.sourceRank),
    }
}

export function sanitizeDiscoveryCandidate(candidate) {
    let media = candidate && candidate.media
    if (!media || !VALID_KINDS.has(media.kind)) return null
    let provider = text(media.provider)
    let id = text(media.id)
    let title = text(media.title)
    let poster = httpUrl(media.poster)
    if (!provider || !id || !title || !poster) return null

    let backdropCandidates = (Array.isArray(media.backdropCandidates)
        ? media.backdropCandidates
        : [])
        .map(httpUrl)
        .filter(Boolean)
        .filter((url, index, values) => values.indexOf(url) === index)
        .slice(0, 2)
    if (!backdropCandidates.includes(poster)) backdropCandidates.push(poster)

    return {
        source: text(candidate.source, provider),
        media: {
            key: text(media.key, `${provider}:${id}`),
            provider,
            id,
            kind: media.kind,
            title,
            originalTitle: text(media.originalTitle),
            year: finiteNumber(media.year),
            yearEnd: finiteNumber(media.yearEnd),
            poster,
            backdrop: httpUrl(media.backdrop) || backdropCandidates[0] || poster,
            backdropCandidates,
            subtitle: text(media.subtitle),
            description: text(media.description).slice(0, 600),
            durationMinutes: finiteNumber(media.durationMinutes),
            episodes: finiteNumber(media.episodes),
            releaseType: text(media.releaseType),
            releaseStatus: text(media.releaseStatus),
            isEpisodic: typeof media.isEpisodic === 'boolean'
                ? media.isEpisodic
                : (media.kind === 'series' ? true : (media.kind === 'movie' ? false : null)),
            ids: normalizedIds(media.ids),
        },
        signals: normalizedSignals(candidate.signals),
    }
}

function matchesSourceKind(candidate, sourceName) {
    if (!candidate || !candidate.media) return false
    return sourceName === 'anime'
        ? candidate.media.kind === 'anime'
        : ['movie', 'series'].includes(candidate.media.kind)
}

function sanitizeSource(source, sourceName) {
    if (!source || typeof source !== 'object') return null
    let fetchedAt = finiteNumber(source.fetchedAt)
    if (!(fetchedAt > 0) || !Array.isArray(source.items)) return null
    let items = source.items
        .map(sanitizeDiscoveryCandidate)
        .filter(Boolean)
        .filter((candidate) => matchesSourceKind(candidate, sourceName))
        .slice(0, CACHE_SOURCE_LIMIT)
    return items.length ? { fetchedAt, items } : null
}

export function validateCacheRecord(value) {
    if (!value || value.version !== WORTH_WATCHING_CACHE_VERSION) return null
    let sources = value.sources || {}
    let cinema = sanitizeSource(sources.cinema, 'cinema')
    let anime = sanitizeSource(sources.anime, 'anime')
    if (!cinema && !anime) return null
    return {
        version: WORTH_WATCHING_CACHE_VERSION,
        sources: { cinema, anime },
    }
}

export function dayKeyFor(value = Date.now()) {
    let date = new Date(value)
    if (!Number.isFinite(date.getTime())) return '1970-01-01'
    return date.toISOString().slice(0, 10)
}

export function stableHash(value) {
    let hash = 2166136261
    let string = String(value || '')
    for (let index = 0; index < string.length; index++) {
        hash ^= string.charCodeAt(index)
        hash = Math.imul(hash, 16777619)
    }
    return hash >>> 0
}

function normalizedTitle(value) {
    return String(value || '')
        .normalize('NFKC')
        .trim()
        .toLowerCase()
        .replace(/ё/g, 'е')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .trim()
}

function candidateKey(candidate) {
    let media = candidate && candidate.media || {}
    return text(media.key, `${media.provider}:${media.id}`)
}

function titleIdentity(candidate) {
    let media = candidate && candidate.media || {}
    return `${normalizedTitle(media.title)}:${media.year || ''}`
}

function candidateReleaseYear(candidate, currentYear) {
    let media = candidate.media
    if (media.kind === 'series') {
        if (media.yearEnd) return media.yearEnd
        if (candidate.signals.status === 'ongoing') return currentYear
    }
    return media.year
}

function plausibleRelease(candidate, now) {
    let currentYear = new Date(now).getUTCFullYear()
    let year = candidateReleaseYear(candidate, currentYear)
    if (!(year >= currentYear - RECENT_YEARS && year <= currentYear)) return false

    let premiere = candidate.signals.premiereAt
    if (premiere) {
        let premiereAt = new Date(premiere).getTime()
        if (Number.isFinite(premiereAt) && premiereAt > now) return false
    }
    return candidate.signals.status !== 'announced'
}

function weightedAverage(values) {
    let totalWeight = values.reduce((total, item) => total + item.weight, 0)
    if (!(totalWeight > 0)) return null
    return values.reduce((total, item) => total + item.rating * item.weight, 0) / totalWeight
}

function cinemaEvidence(candidate) {
    let ratings = candidate.signals.ratings
    let votes = candidate.signals.votes
    // Cinemeta publishes popularity and rating, but no vote counts. Keep its
    // confidence bonus at zero instead of inventing evidence for this source.
    if (candidate.source === 'cinemeta-top' && !votes.imdb && !votes.kinopoisk) {
        return ratings.imdb >= 7.5 && candidate.signals.popularity > 0
            ? { rating: ratings.imdb, votes: 0 }
            : null
    }
    let sources = [
        { rating: ratings.kinopoisk, votes: votes.kinopoisk, minimumVotes: 1000 },
        { rating: ratings.imdb, votes: votes.imdb, minimumVotes: 3000 },
    ].filter((item) => item.rating > 0 && item.votes >= item.minimumVotes)
    if (!sources.length) return null
    if (sources.some((item) => item.votes >= 5000 && item.rating < 5.5)) return null

    let rating = weightedAverage(sources.map((item) => ({
        ...item,
        weight: Math.min(6, Math.log10(item.votes + 1)),
    })))
    if (!(rating >= 7)) return null
    return {
        rating,
        votes: sources.reduce((total, item) => total + item.votes, 0),
    }
}

function animeEvidence(candidate) {
    let ratings = candidate.signals.ratings
    let votes = candidate.signals.votes
    let shikimoriRating = ratings.shikimori
    let shikimoriVotes = votes.shikimori || 0

    if (shikimoriRating >= 7.5 && shikimoriVotes >= 500) {
        return { rating: shikimoriRating, votes: shikimoriVotes }
    }

    let confirmed = ratings.kinopoisk >= 7.5 && votes.kinopoisk >= 3000
        || ratings.imdb >= 7.5 && votes.imdb >= 5000
    if (shikimoriRating >= 7.2 && shikimoriVotes >= 300 && confirmed) {
        return {
            rating: shikimoriRating,
            votes: shikimoriVotes + (votes.kinopoisk || 0) + (votes.imdb || 0),
        }
    }
    return null
}

function scoreCandidate(candidate, now) {
    if (!candidate || !plausibleRelease(candidate, now)) return null
    if (LOW_PLAYBACK_QUALITY.test(candidate.signals.playbackQuality || '')) return null

    let evidence = candidate.media.kind === 'anime'
        ? animeEvidence(candidate)
        : cinemaEvidence(candidate)
    if (!evidence) return null

    let currentYear = new Date(now).getUTCFullYear()
    let releaseYear = candidateReleaseYear(candidate, currentYear)
    let recency = Math.max(0, Math.min(1, 1 - (currentYear - releaseYear) / RECENT_YEARS))
    let confidence = Math.max(0, Math.min(1, Math.log10(evidence.votes + 1) / 6))
    let recommendationScore = evidence.rating * 0.82 + confidence + recency * 0.8

    return {
        candidate,
        key: candidateKey(candidate),
        titleIdentity: titleIdentity(candidate),
        recommendationScore,
        media: {
            ...candidate.media,
            score: Math.round(evidence.rating * 10) / 10,
        },
    }
}

function rankCandidates(values, now) {
    let seenKeys = new Set()
    let seenTitles = new Set()
    return (values || [])
        .map(sanitizeDiscoveryCandidate)
        .filter(Boolean)
        .map((candidate) => scoreCandidate(candidate, now))
        .filter(Boolean)
        .sort((first, second) => second.recommendationScore - first.recommendationScore
            || first.key.localeCompare(second.key, 'ru'))
        .filter((entry) => {
            if (seenKeys.has(entry.key) || seenTitles.has(entry.titleIdentity)) return false
            seenKeys.add(entry.key)
            seenTitles.add(entry.titleIdentity)
            return true
        })
}

export function allocateSlots(size, cinemaAvailable, animeAvailable, cinemaShare = WORTH_WATCHING_CINEMA_SHARE) {
    let maximum = Math.max(0, Math.floor(Number(size) || 0))
    let cinemaCapacity = Math.max(0, Math.floor(Number(cinemaAvailable) || 0))
    let animeCapacity = Math.max(0, Math.floor(Number(animeAvailable) || 0))
    maximum = Math.min(maximum, cinemaCapacity + animeCapacity)
    if (!maximum) return { total: 0, cinema: 0, anime: 0, ratioPreserved: false }
    let normalizedShare = Math.max(0, Math.min(1, Number(cinemaShare) || 0))
    let cinemaTarget = Math.round(maximum * normalizedShare)
    let animeTarget = maximum - cinemaTarget
    let cinema = Math.min(cinemaTarget, cinemaCapacity)
    let anime = Math.min(animeTarget, animeCapacity)
    let remaining = maximum - cinema - anime

    if (remaining > 0) {
        let cinemaExtra = Math.min(remaining, cinemaCapacity - cinema)
        cinema += cinemaExtra
        remaining -= cinemaExtra
    }
    if (remaining > 0) anime += Math.min(remaining, animeCapacity - anime)

    return {
        total: cinema + anime,
        cinema,
        anime,
        ratioPreserved: cinema === cinemaTarget && anime === animeTarget,
    }
}

export function selectRotating(ranked, count, dayKey, lane) {
    let values = Array.isArray(ranked) ? ranked : []
    let target = Math.max(0, Math.min(values.length, Math.floor(Number(count) || 0)))
    if (!target || values.length <= target) return values.slice(0, target)

    let coreCount = Math.min(target, Math.ceil(target * 0.7))
    let rotationCount = target - coreCount
    let core = values.slice(0, coreCount)
    let windowSize = Math.max(rotationCount * 3, 6)
    let rotation = values.slice(coreCount, coreCount + windowSize)
        .map((entry) => ({
            entry,
            order: stableHash(`${dayKey}|${lane}|${entry.key}`),
        }))
        .sort((first, second) => first.order - second.order || first.entry.key.localeCompare(second.entry.key, 'ru'))
        .slice(0, rotationCount)
        .map(({ entry }) => entry)
    let selected = new Set([...core, ...rotation].map((entry) => entry.key))
    let missing = target - core.length - rotation.length
    let fallback = missing > 0
        ? values.filter((entry) => !selected.has(entry.key)).slice(0, missing)
        : []
    return [...core, ...rotation, ...fallback]
}

function interleave(first, second) {
    let firstValues = [...(first || [])]
    let secondValues = [...(second || [])]
    let total = firstValues.length + secondValues.length
    let secondShare = total ? secondValues.length / total : 0
    let result = []
    let firstIndex = 0
    let secondIndex = 0

    while (result.length < total) {
        let desiredSecond = Math.round((result.length + 1) * secondShare)
        let useSecond = secondIndex < secondValues.length && secondIndex < desiredSecond
        if (useSecond || firstIndex >= firstValues.length) result.push(secondValues[secondIndex++])
        else result.push(firstValues[firstIndex++])
    }
    return result
}

function fillCinema(ranked, count, dayKey) {
    let movies = ranked.filter((entry) => entry.media.kind === 'movie')
    let series = ranked.filter((entry) => entry.media.kind === 'series')
    let targetSeries = Math.min(series.length, Math.round(count / 3))
    let targetMovies = Math.min(movies.length, count - targetSeries)
    let remaining = count - targetMovies - targetSeries
    if (remaining > 0) {
        let extraSeries = Math.min(remaining, series.length - targetSeries)
        targetSeries += extraSeries
        remaining -= extraSeries
    }
    if (remaining > 0) targetMovies += Math.min(remaining, movies.length - targetMovies)

    let selectedMovies = selectRotating(movies, targetMovies, dayKey, 'movies')
    let selectedSeries = selectRotating(series, targetSeries, dayKey, 'series')
    return interleave(selectedMovies, selectedSeries)
}

export function buildWorthWatchingFeed({ cinema = [], anime = [] } = {}, {
    size = WORTH_WATCHING_SIZE,
    cinemaShare = WORTH_WATCHING_CINEMA_SHARE,
    now = Date.now(),
    dayKey = dayKeyFor(now),
} = {}) {
    let rankedAnime = rankCandidates(anime, now)
        .filter((entry) => entry.media.kind === 'anime')
    let animeTitles = new Set(rankedAnime.map((entry) => entry.titleIdentity))
    let rankedCinema = rankCandidates(cinema, now)
        .filter((entry) => ['movie', 'series'].includes(entry.media.kind))
        .filter((entry) => !animeTitles.has(entry.titleIdentity))
    let allocation = allocateSlots(size, rankedCinema.length, rankedAnime.length, cinemaShare)
    let selectedCinema = fillCinema(rankedCinema, allocation.cinema, dayKey)
    let selectedAnime = selectRotating(rankedAnime, allocation.anime, dayKey, 'anime')
    let entries = interleave(selectedCinema, selectedAnime)

    return {
        dayKey,
        allocation,
        items: entries.map((entry) => entry.media),
    }
}

function defaultStorage() {
    if (typeof window === 'undefined') return null
    try {
        return window.localStorage
    } catch (_) {
        return null
    }
}

function emptyRecord() {
    return {
        version: WORTH_WATCHING_CACHE_VERSION,
        sources: { cinema: null, anime: null },
    }
}

export function createWorthWatchingFeedService({
    fetchCinema,
    fetchAnime,
    storage,
    now = () => Date.now(),
    config = {},
} = {}) {
    let settings = {
        size: WORTH_WATCHING_SIZE,
        cinemaShare: WORTH_WATCHING_CINEMA_SHARE,
        freshTtl: WORTH_WATCHING_FRESH_TTL,
        maxStale: WORTH_WATCHING_MAX_STALE,
        ...config,
    }
    let memoryRecord = null
    let refreshInFlight = null
    let generation = 0

    function storageTarget() {
        try {
            return typeof storage === 'function' ? storage() : (storage || defaultStorage())
        } catch (_) {
            return null
        }
    }

    function readStoredRecord() {
        if (memoryRecord) return memoryRecord
        let target = storageTarget()
        if (!target) return null
        try {
            memoryRecord = validateCacheRecord(JSON.parse(target.getItem(WORTH_WATCHING_CACHE_KEY)))
        } catch (_) {
            memoryRecord = null
        }
        return memoryRecord
    }

    function usableRecord(at) {
        let record = readStoredRecord()
        if (!record) return null
        let sources = {}
        for (const name of ['cinema', 'anime']) {
            let source = record.sources[name]
            let age = source ? at - source.fetchedAt : -1
            sources[name] = source && age >= 0 && age <= settings.maxStale ? source : null
        }
        return sources.cinema || sources.anime
            ? { version: WORTH_WATCHING_CACHE_VERSION, sources }
            : null
    }

    function writeRecord(record) {
        memoryRecord = validateCacheRecord(record)
        if (!memoryRecord) return
        let target = storageTarget()
        if (!target) return
        try {
            target.setItem(WORTH_WATCHING_CACHE_KEY, JSON.stringify(memoryRecord))
        } catch (_) {
            // Private mode and older TV browsers may reject storage writes.
        }
    }

    function sourceFresh(source, at) {
        let age = source ? at - source.fetchedAt : -1
        return Boolean(source && age >= 0 && age <= settings.freshTtl)
    }

    function compose(record, at) {
        if (!record) return null
        let result = buildWorthWatchingFeed({
            cinema: record.sources.cinema && record.sources.cinema.items || [],
            anime: record.sources.anime && record.sources.anime.items || [],
        }, { size: settings.size, cinemaShare: settings.cinemaShare, now: at })
        if (!result.items.length) return null
        let fetchedAt = Math.max(
            record.sources.cinema && record.sources.cinema.fetchedAt || 0,
            record.sources.anime && record.sources.anime.fetchedAt || 0
        )
        return {
            ...result,
            fetchedAt,
            stale: !sourceFresh(record.sources.cinema, at) || !sourceFresh(record.sources.anime, at),
        }
    }

    function read() {
        let at = Number(now()) || Date.now()
        return compose(usableRecord(at), at)
    }

    function refresh({ force = false } = {}) {
        if (refreshInFlight) return refreshInFlight
        let refreshGeneration = generation
        let at = Number(now()) || Date.now()
        let record = usableRecord(at) || emptyRecord()
        let cinemaFresh = sourceFresh(record.sources.cinema, at)
        let animeFresh = sourceFresh(record.sources.anime, at)
        if (!force && cinemaFresh && animeFresh) {
            let snapshot = compose(record, at)
            return snapshot
                ? Promise.resolve(snapshot)
                : Promise.reject(new Error('Worth-watching feed has no eligible items'))
        }

        let tasks = []
        if ((force || !cinemaFresh) && typeof fetchCinema === 'function') {
            tasks.push({
                name: 'cinema',
                promise: Promise.resolve().then(() => fetchCinema({ now: at, dayKey: dayKeyFor(at) })),
            })
        }
        if ((force || !animeFresh) && typeof fetchAnime === 'function') {
            tasks.push({
                name: 'anime',
                promise: Promise.resolve().then(() => fetchAnime({ now: at, dayKey: dayKeyFor(at) })),
            })
        }
        if (!tasks.length) {
            let snapshot = compose(record, at)
            return snapshot
                ? Promise.resolve(snapshot)
                : Promise.reject(new Error('Worth-watching feed is unavailable'))
        }

        let operation = Promise.allSettled(tasks.map((task) => task.promise)).then((settled) => {
            let nextRecord = {
                version: WORTH_WATCHING_CACHE_VERSION,
                sources: { ...record.sources },
            }
            let updated = false
            let errors = []
            settled.forEach((result, index) => {
                let name = tasks[index].name
                if (result.status === 'fulfilled') {
                    let items = (Array.isArray(result.value) ? result.value : [])
                        .map(sanitizeDiscoveryCandidate)
                        .filter(Boolean)
                        .filter((candidate) => matchesSourceKind(candidate, name))
                        .slice(0, CACHE_SOURCE_LIMIT)
                    if (items.length) {
                        nextRecord.sources[name] = { fetchedAt: at, items }
                        updated = true
                        return
                    }
                    errors.push(new Error(`${name} discovery returned no usable items`))
                    return
                }
                errors.push(result.reason)
            })

            if (refreshGeneration !== generation) {
                throw new Error('Worth-watching refresh was invalidated')
            }
            let snapshot = compose(updated ? nextRecord : record, at)
            if (snapshot) {
                if (updated) writeRecord(nextRecord)
                return snapshot
            }
            throw errors[0] || new Error('Worth-watching feed is unavailable')
        })
        let trackedOperation = operation.finally(() => {
            if (refreshInFlight === trackedOperation) refreshInFlight = null
        })
        refreshInFlight = trackedOperation
        return refreshInFlight
    }

    function invalidate() {
        generation++
        refreshInFlight = null
        memoryRecord = null
        let target = storageTarget()
        if (!target) return
        try {
            target.removeItem(WORTH_WATCHING_CACHE_KEY)
        } catch (_) {
            // Storage is optional.
        }
    }

    return { read, refresh, invalidate }
}
